import {getAuthParams} from 'providers/user/utils';
import {DEVICE_SOURCE_KEY} from 'utils/deviceSource';
import {language} from 'helpers/stableValues';
import {sessionItems} from 'ferries/helpers/common';
import {getPartnerCode} from 'helpers/partner-code';

const sendTrackingEvent = (event, data) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event,
    ...data
  });
};

const getTripType = (routes) => {
  const count = routes.length;
  if (count === 2) {
    if (routes[0].departurePortCode === routes[1].arrivalPortCode &&
      routes[0].arrivalPortCode === routes[1].departurePortCode) {
      return 'roundTrip';
    } else {
      return 'multiTrip';
    }
  }

  if (count > 2) {
    return 'multiTrip';
  }

  return 'oneWay';
};

const getItineraryRoutes = (itinerary) => itinerary.map(({segments}) => {
  const {route, transport: {carriers: {operating}}} = segments[0];
  const {departure, arrival} = route;

  return ({
    departureDate: departure.datetime.format('YYYY-MM-DD'),
    arrivalDate: arrival.datetime.format('YYYY-MM-DD'),
    departurePortCode: departure.code,
    arrivalPortCode: arrival.code,
    company: operating.name
  });
});

const bookingStepEvent = ({event, screen, itinerary}) => {
  const resourceId = sessionStorage.getItem(sessionItems.resourceId);
  const userAccount = getAuthParams();
  const cuid = localStorage.getItem('cuid');
  const userId = userAccount?.uuid ?? cuid;

  const {seating: {passengers, vehicles, pets}} = itinerary[0].segments[0];
  const isCabinAvailable = itinerary.some(({cabin}) => cabin.availability);
  const isVehicleAvailable = itinerary.some(({vehicle}) => vehicle.availability);
  const nofTickets = passengers.length + vehicles.length + pets.length;
  const isCabinSelected = itinerary.some(({segments}) =>
    segments[0].seating.passengers.some(({reservationType}) => reservationType !== 'normal'));
  const isAccommodationUpgrade = itinerary.some(({segments}) => {
    const {seating: {passengers}, facilities: {seat}} = segments[0];
    const pricesOfSeats = seat.map(({price}) => price.total);
    const minPriceOfSeats = Math.min(...pricesOfSeats);
    const classCodeWithMinPrice = seat.find(({price}) => price.total === minPriceOfSeats)?.code;

    return Boolean(passengers.find(({classCode}) => classCode !== classCodeWithMinPrice));
  });

  const routes = getItineraryRoutes(itinerary);
  const firstRoute = routes[0];
  const lastRoute = routes[routes.length - 1];
  const tripType = getTripType(routes);
  const fullRoute = routes.map(({departurePortCode, arrivalPortCode}) =>
    `${departurePortCode} - ${arrivalPortCode}`).join(' | ');
  const companies = routes.map(({company}) => company);
  const uniqCompanies = [...new Set(companies)];

  const isTicketSelection = event === 'booking_step_ticket_selection';
  const isPayment = event === 'booking_step_payment';
  const uniqueTripIdentifier = sessionStorage.getItem('uniqueTripIdentifier') ?? '';

  sendTrackingEvent(
    event,
    Object.assign({
      locale: language,
      screen,
      partner_code: getPartnerCode(),
      platform: sessionStorage.getItem(DEVICE_SOURCE_KEY) ?? null,
      outbound_port_code: firstRoute.departurePortCode,
      inbound_port_code: lastRoute.arrivalPortCode,
      outbound_date: firstRoute.departureDate,
      trip_type: tripType,
      trip_id: uniqueTripIdentifier,
      full_route_code: fullRoute,
      route_code: `${firstRoute.departurePortCode} - ${firstRoute.arrivalPortCode}`,
      is_login_user: Boolean(userAccount?.uuid),
      user_id: userId,
      resource_id: resourceId,
      company_name: uniqCompanies.join(' | '),
      google_business_vertical: 'travel'
    },
    isTicketSelection && {
      cabin_available: isCabinAvailable,
      vehicle_available: isVehicleAvailable
    },
    isPayment && {
      cabin_selected: isCabinSelected,
      vehicle_selected: Boolean(vehicles.length),
      ticket_number: nofTickets,
      accommodation_upgrade: isAccommodationUpgrade
    })
  );
};

const ticketControlOptionsEvent = ({screen, controlCategory, userAction}) => {
  const resourceId = sessionStorage.getItem(sessionItems.resourceId);
  const userAccount = getAuthParams();
  const cuid = localStorage.getItem('cuid');
  const userId = userAccount?.uuid ?? cuid;

  sendTrackingEvent(
    'ticket_control_options',
    {
      control_category: controlCategory,
      user_action: userAction,
      locale: language,
      screen,
      partner_code: getPartnerCode(),
      is_login_user: Boolean(userAccount?.uuid),
      user_id: userId, 
      results_id: resourceId
    }
  );
};

const chatActionEvent = ({event}) => {
  const userAccount = getAuthParams();
  const cuid = localStorage.getItem('cuid');
  const userId = userAccount?.uuid ?? cuid;

  sendTrackingEvent(
    'ticket_control_options',
    {
      event,
      locale: language,
      screen: window.location.pathname.split('/').filter(Boolean).pop(),
      platform: sessionStorage.getItem(DEVICE_SOURCE_KEY) ?? null,
      user_id: userId,
      is_login_user: Boolean(userAccount?.uuid)
    }
  );
};

export {
  bookingStepEvent,
  ticketControlOptionsEvent,
  chatActionEvent
};
